// Kjuup + Microsoft Teams in one new chat: the member picker (MemberSelection, AddProjectChatUser)
// splits contacts into "Kjuup contacts" and "Teams contacts". What the selection creates, the
// POST /teams/bridges payload and the bridge flags on rows and group info live here. Pure: no
// React, no store, no API.
import { isTeamsPayload } from "./teamsCore";

/** Most Teams people one chat may hold (same limit as POST /teams/chats). */
export const TEAMS_MEMBERS_MAX = 50;

/**
 * What the picked people create:
 *   'none'      nobody picked (the Kjuup flow decides, as before)
 *   'kjuup'     Kjuup contacts only: a Kjuup chat / group exactly as before
 *   'teams'     Teams contacts only: a Teams chat (POST /teams/chats)
 *   'combined'  both: a Kjuup group (POST /group, Kjuup ids only) + its Teams copy (POST /teams/bridges)
 */
export function classifyChatSelection(kjuupIds, teamsPeople) {
  const kjuup = Array.isArray(kjuupIds) ? kjuupIds.filter((id) => id != null && id !== "").length : 0;
  const teams = Array.isArray(teamsPeople) ? teamsPeople.filter((p) => p && p.aadUserId).length : 0;
  if (kjuup && teams) return "combined";
  if (teams) return "teams";
  if (kjuup) return "kjuup";
  return "none";
}

/** Name of a Teams person in lists and chips. */
export const teamsPersonName = (p) => (p && (p.displayName || p.email)) || "Microsoft Teams";

const personKey = (p) => (p && p.aadUserId ? String(p.aadUserId).toLowerCase() : "");

/** Teams people without duplicates (same aadUserId, any case) and without rows lacking an id. First one wins. */
export function uniqueTeamsPeople(list) {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((p) => {
    const key = personKey(p);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(p);
  });
  return out;
}

/**
 * The Teams tab's list: the picked people the current search no longer returns first (so a
 * selection stays visible and removable), then the search results, without duplicates.
 */
export function mergeTeamsPeople(results, selected) {
  const found = new Set(uniqueTeamsPeople(results).map(personKey));
  const kept = uniqueTeamsPeople(selected).filter((p) => !found.has(personKey(p)));
  return uniqueTeamsPeople([...kept, ...(Array.isArray(results) ? results : [])]);
}

/** True when `person` is in `selected` (by aadUserId). */
export const isTeamsPersonSelected = (selected, person) =>
  !!personKey(person) && (Array.isArray(selected) ? selected : []).some((s) => personKey(s) === personKey(person));

/**
 * Adds or removes `person`. → { list, error } — error 'too_many' (list unchanged) when adding
 * would pass `max`.
 */
export function toggleTeamsPerson(selected, person, max = TEAMS_MEMBERS_MAX) {
  const list = uniqueTeamsPeople(selected);
  if (!personKey(person)) return { list, error: null };
  if (isTeamsPersonSelected(list, person)) {
    return { list: list.filter((s) => personKey(s) !== personKey(person)), error: null };
  }
  if (list.length >= max) return { list, error: "too_many" };
  return { list: [...list, person], error: null };
}

/** POST /teams/bridges members: [{ aadUserId, displayName, email }] (unique, with an id). */
export function bridgeMembersPayload(people) {
  return uniqueTeamsPeople(people).map((p) => ({
    aadUserId: String(p.aadUserId),
    displayName: p.displayName || null,
    email: p.email || null,
  }));
}

/** POST /teams/bridges body for the new group `groupId`; accountId only when one is picked. */
export function buildBridgeRequest({ groupId, accountId = null, people }) {
  return {
    groupId: String(groupId),
    ...(accountId != null && accountId !== "" ? { accountId: String(accountId) } : {}),
    members: bridgeMembersPayload(people),
  };
}

/**
 * Name of a new Teams-only chat for the placeholder row: the typed topic for a group, else the
 * first names ("Anna, Ben, Cem +2"); the person for a 1:1.
 */
export function teamsChatDisplayName(people, topic = "") {
  const list = uniqueTeamsPeople(people);
  if (list.length > 1) {
    const typed = String(topic || "").trim();
    if (typed) return typed;
    return list.slice(0, 3).map(teamsPersonName).join(", ") + (list.length > 3 ? ` +${list.length - 3}` : "");
  }
  return teamsPersonName(list[0]);
}

/**
 * The Teams copy of a native Kjuup group (`teamsBridge` on a Messages row, its nested group or
 * group info): { webUrl, ownerUserId, accountEmail }, else null. Teams mirrors (source 'teams')
 * are never bridges: they keep their own Teams mode.
 */
export function teamsBridgeOf(x) {
  if (!x || typeof x !== "object" || isTeamsPayload(x)) return null;
  const candidates = [x.teamsBridge, x.group && x.group.teamsBridge, x.groupInfo && x.groupInfo.teamsBridge];
  const hit = candidates.find((b) => b && typeof b === "object");
  if (!hit) return null;
  return {
    webUrl: hit.webUrl || null,
    ownerUserId: hit.ownerUserId != null ? hit.ownerUserId : null,
    accountEmail: hit.accountEmail || null,
  };
}

/** True for a group member who is a Teams person without a Kjuup login (no profile, no DM). */
export const isTeamsGuest = (u) => !!u && typeof u === "object" && u.isTeamsGuest === true;

/**
 * The chat header's "Kjuup + Teams" mark for a group with a Teams copy, from the payloads the
 * pane has (group info, the open-chat entry), else null.
 */
export function teamsBridgeLabel(strings, ...sources) {
  const bridged = sources.some((s) => teamsBridgeOf(s) != null);
  if (!bridged) return null;
  return (strings && strings.combined_chat_label) || "Kjuup + Teams";
}

/**
 * The picker's Teams side from its inputs (a helper so the pickers' bodies stay free of branches
 * before their hooks): `tabOn` once a writable account is loaded, `picked` = the Teams people
 * that count (none without the tab).
 */
export function teamsPickerState({ enabled, accounts, selected }) {
  const tabOn = !!enabled && Array.isArray(accounts) && accounts.length > 0;
  return { tabOn, picked: tabOn && Array.isArray(selected) ? selected : [] };
}
